import * as React from 'react'
import axios from 'axios'
import { faChevronDownSolid, FaIcon } from '@/lib/fa'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { TimesheetSettlementType } from '@/app/constants/configuration/timesheetSettlementType'
import { getTimesheetSettlementTypeName } from '@/app/common/converters/configuration/timesheetSettlementTypeToTextConverter'
import { useQueryCurrentSettlementCycle } from '@/lib/api/company/configuration/timesheet-basic/useQueryCurrentSettlementCycle'
import { useMutationCurrentSettlementType } from '@/lib/api/company/configuration/timesheet-basic/useMutationCurrentSettlementType'

const PERIOD_LENGTHS = [1, 2, 3, 4]
const toSettlementType = (months: number): TimesheetSettlementType =>
  (months - 1) as TimesheetSettlementType

interface Props {
  companyId: string
  size?: 'sm' | 'md'
  className?: string
  onChanged?: (periodLengthInMonths: number) => void
}

export const SettlementPeriodSwitcher: React.FC<Props> = ({
  companyId,
  size = 'sm',
  className,
  onChanged,
}) => {
  const { t, tSafe } = useLocalize()
  const confirm = useConfirm()

  const { data: cycle, isLoading } = useQueryCurrentSettlementCycle(companyId)
  const { mutateAsync, isPending } = useMutationCurrentSettlementType(companyId)

  const activeMonths = cycle?.periodLengthInMonths ?? null
  const isCompact = size === 'sm'
  const isBusy = isPending || isLoading

  const [isOpen, setIsOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) return undefined

    const handleMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setIsOpen(false)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const getLabel = (months: number) =>
    getTimesheetSettlementTypeName(toSettlementType(months), t)

  const handlePick = async (months: number) => {
    setIsOpen(false)
    if (!companyId || isPending || months === activeMonths) return

    const ok = await confirm({
      title: t('configuration.timesheet.changeSettlementPeriodTypeTitle'),
      message: t('configuration.timesheet.changeSettlementPeriodTypeDetails'),
    })
    if (!ok) return

    try {
      await mutateAsync(toSettlementType(months))
      toast.success({ text: t('configuration.data-updated') })
      onChanged?.(months)
    } catch (e) {
      const code = axios.isAxiosError(e)
        ? Array.isArray(e.response?.data)
          ? e.response?.data[0]?.code
          : null
        : null
      toast.error({
        text: code ? tSafe(`errorCode.${code}`, code) : 'Unhandled Error',
        autoCloseMs: 5000,
      })
    }
  }

  const title = t('configuration-timesheet.settlement-period-length')

  if (isCompact) {
    return (
      <div ref={rootRef} className={`tw-relative ${className ?? ''}`}>
        <button
          type="button"
          title={title}
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={isBusy}
          className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-2.5 tw-text-xs tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
            isOpen
              ? ' tw-bg-secondary tw-text-primary'
              : ' tw-bg-white tw-text-content-base/80 hover:tw-bg-gray-50'
          }`}
        >
          <span className="tw-whitespace-nowrap tw-font-semibold">
            {activeMonths ? getLabel(activeMonths) : '—'}
          </span>
          {isPending ? (
            <span className="tw-h-3 tw-w-3 tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-primary" />
          ) : (
            <FaIcon
              icon={faChevronDownSolid}
              className={`tw-h-2.5 tw-w-2.5 tw-text-gray-400 tw-transition-transform ${
                isOpen ? 'tw-rotate-180' : ''
              }`}
            />
          )}
        </button>

        {isOpen && (
          <div className="tw-absolute tw-bottom-full tw-left-0 tw-z-50 tw-mb-1 tw-min-w-full tw-overflow-hidden tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-py-1 tw-shadow-lg">
            <div className="tw-whitespace-nowrap tw-px-3 tw-pb-1 tw-pt-0.5 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400">
              {title}
            </div>

            {PERIOD_LENGTHS.map((months) => {
              const isActive = activeMonths === months
              return (
                <button
                  key={months}
                  type="button"
                  onClick={() => handlePick(months)}
                  disabled={isPending}
                  className={`tw-flex tw-w-full tw-items-center tw-whitespace-nowrap tw-px-3 tw-py-1.5 tw-text-left tw-text-xs tw-transition-colors disabled:tw-cursor-not-allowed ${
                    isActive
                      ? 'tw-bg-secondary tw-font-semibold tw-text-primary'
                      : 'tw-text-content-base/80 hover:tw-bg-gray-100'
                  }`}
                >
                  {getLabel(months)}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`tw-flex tw-flex-col tw-items-center tw-gap-1.5 ${className ?? ''}`}
    >
      <span className="tw-text-sm tw-text-gray-500">{title}</span>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1.5">
        {PERIOD_LENGTHS.map((months) => {
          const label = getLabel(months)
          const isActive = activeMonths === months
          return (
            <button
              key={months}
              type="button"
              title={label}
              onClick={() => handlePick(months)}
              disabled={isBusy}
              className={`tw-rounded-lg tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
                isActive
                  ? 'tw-border-primary tw-bg-secondary tw-font-semibold tw-text-primary'
                  : 'tw-border-gray-200 tw-bg-transparent tw-text-content-base/70 hover:tw-bg-gray-100'
              }`}
            >
              {label}
            </button>
          )
        })}

        {isPending && (
          <span className="tw-h-3 tw-w-3 tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-primary" />
        )}
      </div>
    </div>
  )
}

export default SettlementPeriodSwitcher
